export type ValueFormat = 'text' | 'number' | 'percent' | 'money' | 'date' | 'state';

const STATE_LABELS: Readonly<Record<string, string>> = {
  Live: 'live',
  Scheduled: 'scheduled',
  Paused: 'paused',
  Ended: 'ended',
  Draft: 'draft',
  Published: 'published',
  Unpublished: 'unpublished',
  Finished: 'finished',
  Regular: 'regular',
  ABTesting: 'ab-test',
  Survey: 'survey',
  Enabled: 'enabled',
  Disabled: 'disabled',
  Archived: 'archived',
};

const TOOL_ERROR_CATEGORIES: ReadonlySet<string> = new Set([
  'not_found',
  'permission_denied',
  'validation',
  'conflict',
  'transient',
  'partial_failure',
  'internal',
]);

// Tools with a friendly, localized status label under `agent.tool-label.<name>`; anything not listed
// falls back to a humanized name. Keep in sync with the `agent.tool-label` block in the lang files.
const LABELLED_TOOLS: ReadonlySet<string> = new Set([
  'sites_get',
  'sites_list',
  'sites_resolve',
  'audiences_list',
  'audiences_create',
  'integrations_list',
  'campaigns_list',
  'campaigns_getSettings',
  'campaigns_getText',
  'campaigns_updateText',
  'campaigns_publish',
  'campaigns_unpublish',
  'campaigns_createFromTemplate',
  'campaigns_copy',
  'campaigns_updateSettings',
  'campaigns_updateTargeting',
  'campaigns_duplicateWidget',
  'campaigns_getStructure',
  'campaigns_planChange',
  'campaigns_applyChange',
  'campaigns_restoreVersion',
  'campaigns_planFormIntegration',
  'campaigns_connectFormIntegration',
  'campaigns_disconnectFormIntegration',
  'campaignTemplates_list',
  'benchmarks_get',
  'site_overview',
  'onsite_campaigns_summary',
  'onsite_campaigns_widget_breakdown',
  'onsite_campaigns_trend',
  'widgetAnalytics_revenueAudienceGraph',
  'top_campaigns',
  'commerce_query',
  'traffic_query',
  'campaign_query',
  'feedback_query',
  'signup_query',
  'help_search',
]);

export function toolLabel(name: string): string {
  return LABELLED_TOOLS.has(name) ? `agent.tool-label.${name}` : humanizeToolName(name);
}

function humanizeToolName(name: string): string {
  const spaced = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  if (spaced.length === 0) {
    return name;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// The payload's message is written for the model, so the user sees a translated line per category.
export function toolErrorKey(content: unknown): string {
  const category = asObject(content)?.['category'];
  return `agent.tool-error.${typeof category === 'string' && TOOL_ERROR_CATEGORIES.has(category) ? category : 'internal'}`;
}

export function asArray(content: unknown): Record<string, unknown>[] {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
}

export function asObject(content: unknown): Record<string, unknown> | undefined {
  return content && typeof content === 'object' && !Array.isArray(content)
    ? (content as Record<string, unknown>)
    : undefined;
}

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function valueAt(source: Record<string, unknown> | undefined, path: string): unknown {
  let current: unknown = source;
  for (const part of path.split('.')) {
    const container = asObject(current);
    if (!container) {
      return undefined;
    }
    current = container[part];
  }
  return current;
}

export function firstPresent(
  source: Record<string, unknown> | undefined,
  paths: string[],
): { path: string; value: unknown } | undefined {
  for (const path of paths) {
    const value = valueAt(source, path);
    if (value !== null && value !== undefined) {
      return { path, value };
    }
  }
  return undefined;
}

export function formatValue(value: unknown, format: ValueFormat, currency?: string, key?: string): string {
  if (typeof value === 'number' && format === 'percent') {
    return value.toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 1 });
  }
  if (typeof value === 'number' && format === 'money') {
    const amount = formatCell(value, key);
    return currency ? `${amount} ${currency}` : amount;
  }
  if (typeof value === 'string' && format === 'date') {
    return formatDate(value);
  }
  if (typeof value === 'string' && format === 'state' && STATE_LABELS[value]) {
    return `agent.result.state.${STATE_LABELS[value]}`;
  }
  return formatCell(value, key);
}

export function formatChange(change: unknown): string | undefined {
  const relative = asObject(change)?.['relative'];
  if (typeof relative !== 'number') {
    return undefined;
  }
  return relative.toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' });
}

// IDs skip locale grouping: "1,234,567" is wrong for an identifier.
export function formatCell(value: unknown, key?: string): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? '✓' : '';
  }
  if (typeof value === 'number') {
    return isIdKey(key) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (Array.isArray(value)) {
    return value.map((entry) => formatCell(entry, key)).join(', ');
  }
  return String(value);
}

// Narrow on purpose: matches "id" / "…Id" / "…_id" / "…-id" and their plurals, but not words ending in
// "id" or "ids" (valid, grid, bids).
export function isIdKey(key: string | undefined): boolean {
  if (key === undefined) {
    return false;
  }
  return /^ids?$/i.test(key) || /(?:[_-]ids?|[a-z0-9](?:Ids?|IDs?))$/.test(key);
}

// Backend result keys aren't in the i18n catalog, so they are humanized rather than translated.
export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (spaced.length === 0) {
    return key;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function toNumber(value: unknown): number {
  return typeof value === 'number' ? value : Number(value) || 0;
}

export function formatDateLabel(start: string): string {
  return formatIsoDate(start, { month: 'short', day: 'numeric' });
}

function formatDate(value: string): string {
  return formatIsoDate(value, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatIsoDate(value: string, options: Intl.DateTimeFormatOptions): string {
  if (value.length === 0) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  // UTC so a date-only bucket start doesn't shift a day in negative-offset timezones.
  return date.toLocaleDateString(undefined, { ...options, timeZone: 'UTC' });
}
